import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function ModelDistribution() {
  const navigate = useNavigate();

  // Mock data for distribution by stage
  const distributionByStage = {
    labels: [
      '1. Ongoing Model Validation',
      '2. Ongoing Model Development',
      '3. Model Ready for Initial Validation',
      '4. Remediation and Testing',
      '5. Approval',
      '6. Model Redevelopment',
      '7. In Production - Not Validated'
    ],
    datasets: [{
      data: [28, 35, 18, 15, 12, 8, 26],
      backgroundColor: [
        '#4472C4',
        '#ED7D31',
        '#A5A5A5',
        '#FFC000',
        '#5B9BD5',
        '#70AD47',
        '#C5504F'
      ],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Mock data table
  const modelData = [
    { stage: '1. Ongoing Model Validation', count: 28, owner: 'J. Smith', stage2: 'Other', phase: 'Active' },
    { stage: '2. Ongoing Model Development', count: 35, owner: 'M. Johnson', stage2: 'CAD-Tier 3', phase: 'Development' },
    { stage: '3. Model Ready for Initial Validation', count: 18, owner: 'R. Williams', stage2: 'CAD-Tier 2', phase: 'Review' },
    { stage: '4. Remediation and Testing', count: 15, owner: 'L. Davis', stage2: 'CAD-Tier 1', phase: 'Testing' },
    { stage: '5. Approval', count: 12, owner: 'S. Miller', stage2: 'Other', phase: 'Approval' },
    { stage: '6. Model Redevelopment', count: 8, owner: 'T. Brown', stage2: 'CAD-Tier 1', phase: 'Development' },
    { stage: '7. In Production - Not Validated', count: 26, owner: 'K. Wilson', stage2: 'CAD-Tier 3', phase: 'Production' }
  ];

  // Distribution by model carrier
  const distributionByCarrier = {
    labels: ['Vendor Developed', 'Internal Development', 'Hybrid Model', 'Third-party License'],
    datasets: [{
      data: [42, 65, 23, 12],
      backgroundColor: ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Distribution by risk type
  const distributionByRiskType = {
    labels: ['Credit Risk', 'Market Risk', 'Operational Risk', 'Fraud Detection', 'Trading Strategy', 'Other'],
    datasets: [{
      data: [45, 28, 22, 18, 12, 17],
      backgroundColor: ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Distribution by maturity
  const distributionByMaturity = {
    labels: ['Tier 1 (High)', 'Tier 2 (Medium)', 'Tier 3 (Low)', 'Not Rated'],
    datasets: [{
      label: 'Number of Models',
      data: [45, 58, 32, 7],
      backgroundColor: ['#C5504F', '#FFC000', '#70AD47', '#A5A5A5'],
      borderRadius: 4
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 12,
          padding: 10,
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    indexAxis: 'y',
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          stepSize: 20
        }
      }
    }
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <i className="bi bi-pie-chart me-2"></i>
                Model Distribution
              </h2>
              <p className="text-muted mb-0">Distribution Analysis by Various Categories</p>
            </div>
            <button className="btn btn-outline-secondary" onClick={() => navigate('/reports')}>
              <i className="bi bi-arrow-left me-2"></i>
              Quay lại
            </button>
          </div>
        </div>
      </div>

      {/* Main Distribution by Stage */}
      <div className="row g-4 mb-4">
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0">Distribution of Models per Stage</h5>
            </div>
            <div className="card-body">
              <div style={{ height: '350px' }}>
                <Doughnut data={distributionByStage} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0">Model Details by Stage</h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover table-sm mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Stage</th>
                      <th className="text-center">Count</th>
                      <th>Owner</th>
                      <th>Category</th>
                      <th>Phase</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modelData.map((item, index) => (
                      <tr key={index}>
                        <td className="small">{item.stage}</td>
                        <td className="text-center fw-bold">{item.count}</td>
                        <td className="small">{item.owner}</td>
                        <td className="small">{item.stage2}</td>
                        <td>
                          <span className={`badge badge-sm ${
                            item.phase === 'Production' ? 'bg-success' :
                            item.phase === 'Active' ? 'bg-primary' :
                            item.phase === 'Development' ? 'bg-info' :
                            item.phase === 'Testing' ? 'bg-warning' :
                            item.phase === 'Approval' ? 'bg-secondary' :
                            'bg-light text-dark'
                          }`}>
                            {item.phase}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td className="fw-bold">Total</td>
                      <td className="text-center fw-bold">{modelData.reduce((sum, item) => sum + item.count, 0)}</td>
                      <td colSpan="3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Distributions */}
      <div className="row g-4 mb-4">
        {/* Distribution by Model Carrier */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0 small">Distribution by Model Carrier</h5>
            </div>
            <div className="card-body">
              <div style={{ height: '250px' }}>
                <Doughnut data={distributionByCarrier} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Distribution by Risk Type */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0 small">Distribution by Model Risk Type</h5>
            </div>
            <div className="card-body">
              <div style={{ height: '250px' }}>
                <Doughnut data={distributionByRiskType} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* Distribution by Maturity */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0 small">Distribution by Model Maturity</h5>
            </div>
            <div className="card-body">
              <div style={{ height: '250px' }}>
                <Bar data={distributionByMaturity} options={barChartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="row g-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom">
              <h5 className="mb-0">
                <i className="bi bi-graph-up me-2"></i>
                Summary Statistics
              </h5>
            </div>
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-3">
                  <div className="border-end">
                    <h3 className="fw-bold text-primary mb-0">142</h3>
                    <p className="text-muted small mb-0">Total Models</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border-end">
                    <h3 className="fw-bold text-success mb-0">85</h3>
                    <p className="text-muted small mb-0">In Production</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="border-end">
                    <h3 className="fw-bold text-warning mb-0">41</h3>
                    <p className="text-muted small mb-0">In Development</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <h3 className="fw-bold text-secondary mb-0">16</h3>
                  <p className="text-muted small mb-0">Retired</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModelDistribution;
